import {useNavigate, useParams} from "react-router-dom";
import {Box, Divider, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {TaxRateService} from "crqlar-react-common/dist/services/rate/taxRate.service";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredNumberMissing, isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";

const TaxRateDetails = () => {

    //region State

    useTrackScreenVisit('system tax rate details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const taxRateData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return TaxRateService.getTaxRateById(id)
            } else {
                return {
                    name: "",
                    rate: 20,
                    inclusive: true
                }
            }
        },
        create: TaxRateService.createTaxRate,
        update: TaxRateService.updateTaxRate,
        delete: (id) => TaxRateService.deleteTaxRate({id: [id]}),
        onDeleted: () => navigate('/system/taxRates', {replace: true}),
        onSaved: (taxRate, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/system/taxRates/${taxRate.id}`, {replace: true})
            }
        },
        isValid: (taxRate) => {
            return !!taxRate.name?.trim() && taxRate.rate !== undefined && !isNaN(taxRate.rate)
        },
        cleanupBeforeSave: (taxRate) => {
            return {
                ...taxRate,
                name: taxRate.name?.trim()
            }
        }
    }, id)

    const taxRate = taxRateData.item
    const setTaxRate = taxRateData.setItem
    const nameMissing = isRequiredTextMissing(taxRateData, taxRate.name)
    const rateMissing = isRequiredNumberMissing(taxRateData, taxRate.rate)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof TaxRate, value: any) => {
        setTaxRate({...taxRate, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={taxRate.name}
            data={taxRateData}
            back={() => navigate('/system/taxRates')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this tax rate in pricing and billing screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={taxRate.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={`${t('Rate')} %`}
                    tooltip={t('Tax percentage applied when this tax rate is selected.')}
                >
                    <TextField
                        id={'rate'}
                        size={'small'}
                        value={taxRate.rate ?? ''}
                        onChange={(e) => {
                            const nextRate = parseFloat(e.target.value)
                            updateField('rate', isNaN(nextRate) ? undefined : nextRate)
                        }}
                        fullWidth
                        required
                        error={rateMissing}
                        type={'number'}
                    />
                </Property>

                <Property
                    title={t('Included')}
                    tooltip={t('When enabled, entered prices already include this tax. When disabled, tax is added on top.')}
                >
                    <Switch
                        id={'included'}
                        checked={!!taxRate.inclusive}
                        onChange={(e) => updateField('inclusive', e.target.checked)}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete tax rate')}
                description={t('This permanently deletes the tax rate. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={taxRateData.saving}
                onDelete={() => taxRateData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {taxRate.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {basicInfoSection}

                {
                    !!taxRate.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default TaxRateDetails;
